import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons, PayPalMessages, usePayPalScriptReducer } from '@paypal/react-paypal-js';

const API = process.env.REACT_APP_BACKEND_URL;

// One fetch of /api/paypal/config per page load; buttons stay hidden when PayPal isn't configured.
let cfgCache = null;
let cfgPromise = null;
const loadConfig = () => {
  if (cfgCache) return Promise.resolve(cfgCache);
  if (!cfgPromise) cfgPromise = axios.get(`${API}/api/paypal/config`).then((r) => { cfgCache = r.data; return cfgCache; }).catch(() => ({ enabled: false }));
  return cfgPromise;
};
export const usePayPalConfig = () => {
  const [cfg, setCfg] = useState(cfgCache);
  useEffect(() => { let on = true; loadConfig().then((c) => on && setCfg(c)); return () => { on = false; }; }, []);
  return cfg;
};

const scriptOptions = (cfg) => ({ clientId: cfg.client_id, currency: cfg.currency || 'USD', intent: 'capture', components: 'buttons,messages', enableFunding: 'paylater', disableFunding: 'card' });

// Single app-level provider (deferred). The SDK is only downloaded when the first PayPal element appears on a page.
export const PayPalRoot = ({ children }) => (
  <PayPalScriptProvider deferLoading options={{ clientId: 'test', currency: 'USD' }}>{children}</PayPalScriptProvider>
);

let loadRequested = false;
// Call from any PayPal element: loads the SDK once, with the real client id, the first time it's needed.
const usePayPalReady = () => {
  const cfg = usePayPalConfig();
  const [{ isResolved }, dispatch] = usePayPalScriptReducer();
  useEffect(() => {
    if (cfg?.enabled && !loadRequested) { loadRequested = true; dispatch({ type: 'resetOptions', value: scriptOptions(cfg) }); }
  }, [cfg, dispatch]);
  return { enabled: !!cfg?.enabled, ready: !!cfg?.enabled && isResolved };
};

/**
 * PayPal + Pay Later buttons beside the Stripe button.
 * createOrder: async () => paypalOrderId   (your backend creates the pending record + PayPal order)
 * Capture is always done server-side, then we go to the normal success page.
 */
export const PayPalPay = ({ createOrder, disabled, amount, testId = 'paypal-buttons', beforeCreate }) => {
  const { enabled, ready } = usePayPalReady();
  if (!enabled) return null;
  return (
    <div data-testid={testId} style={{ display: 'grid', gap: 6, opacity: disabled ? 0.45 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0.6rem 0 0.2rem' }}>
        <span style={{ flex: 1, height: 1, background: '#1F2A4422' }} />
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1F2A4499', letterSpacing: '0.5px', textTransform: 'uppercase' }}>or pay with PayPal · Pay Later</span>
        <span style={{ flex: 1, height: 1, background: '#1F2A4422' }} />
      </div>
      {ready ? (
        <>
          {amount > 0 && <PayPalMessages amount={amount} pageType="product-details" style={{ layout: 'text', logo: { type: 'inline' }, text: { size: 12 } }} forceReRender={[amount]} />}
          <PayPalButtons
            style={{ layout: 'vertical', shape: 'pill', label: 'pay', height: 45 }}
            forceReRender={[amount]}
            disabled={disabled}
            onClick={(data, actions) => (beforeCreate && beforeCreate() === false ? actions.reject() : actions.resolve())}
            createOrder={async () => {
              try { return await createOrder(); } catch (err) { toast.error(err?.response?.data?.detail || 'Could not start PayPal checkout'); throw err; }
            }}
            onApprove={async (data) => {
              try {
                const r = await axios.post(`${API}/api/paypal/orders/${data.orderID}/capture`);
                window.location.href = r.data.redirect;
              } catch (err) { toast.error(err?.response?.data?.detail || 'PayPal payment could not be completed'); }
            }}
            onCancel={() => toast('No payment was taken.')}
            onError={() => toast.error('PayPal ran into a problem. You can still pay by card above.')}
          />
        </>
      ) : <div data-testid={`${testId}-loading`} style={{ height: 45, borderRadius: 999, background: '#1F2A440D' }} />}
    </div>
  );
};

// "Pay in 4" style messaging next to a course price (PayPal shows it only where the buyer's country supports Pay Later).
export const PayLaterNote = ({ amount, testId = 'paylater-note', style }) => {
  const { enabled, ready } = usePayPalReady();
  if (!enabled || !amount) return null;
  return (
    <div data-testid={testId} style={{ minHeight: 18, ...style }}>
      {ready && <PayPalMessages amount={amount} pageType="product-details" style={{ layout: 'text', logo: { type: 'inline' }, text: { size: 11, color: 'black' } }} forceReRender={[amount]} />}
    </div>
  );
};
